import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import { type GroupMember, Role } from '@/models/group/group-info';
import { useUserId } from '@/stores/auth';
import cn from '@/utils/merge-class-names';

interface Props {
  member: GroupMember;
  canManage?: boolean;
  showBadge?: boolean;
  onClick: (e: MouseEvent) => void;
}

export function GroupAdminListItem({ member, canManage, showBadge, onClick }: Props) {
  const { t } = useTranslation();
  const userId = useUserId();

  const name = member.RemarkName || member.GroupMemberName || member.UserNickName;

  const handleClick = (e: MouseEvent) => {
    if (member.Role === Role.Owner) {
      return;
    }
    if (canManage) {
      onClick(e);
    }
  };

  return (
    <div
      className='flex min-w-0 flex-1 items-center gap-4 focus:bg-primary/20'
      onClick={handleClick}>
      <AvatarWithStatus
        imageUrl={member.UserHeadImg}
        className='size-12'
        size={[48, 48]}
        label={name}
        isSelf={userId === member.UserID}
        isOnline={member.OnLine === 1}
        defaultAvatar='group'
      />

      <div className='line-clamp-1 flex flex-1 flex-col items-stretch justify-center gap-0.5'>
        <h5 className='truncate break-all text-h2  desktop:text-body1'>{name}</h5>
      </div>

      <span
        className={cn('badge badge-outline badge-lg min-w-16 text-xs', {
          'text-secondary': member.Role === Role.Owner,
          'text-primary': member.Role === Role.Admin,
          hidden: !showBadge,
        })}>
        {member.Role === Role.Admin
          ? t('conversation.setting.group.administrator')
          : member.Role === Role.Owner
            ? t('conversation.setting.group.owner')
            : null}
      </span>
    </div>
  );
}
